import React from "react";
import { useTranslation } from "react-i18next";
import { capitalizeFirstLetter, formateDate, showPrice } from "../../../utils/commonfunction";
import { TRANSACTION_PRINT_TYPES } from "../../../utils/Constants";

type TransactionSlip = {
  slip_number?: string | number;
  source?: string;
  cashier_id?: { name?: string };
  tableDetail?: { table_number?: string | number };
  number_of_persons?: number;
  men?: number;
  women?: number;
  payment_method?: string;
  method_name?: string;
  createdAt?: string;
  type?: string;
  subtotal?: number;
  gift_card_applied?: boolean;
  coupon_applied?: boolean;
  great_grand_total?: number;
  amount_after_discount?: number;
  tax_price?: number;
  service_charge?: number;
  taxable_amount_8?: number;
  taxable_amount_10?: number;
  receiptHTML?: string;
};

type SalesSlipViewModalProps = {
  transactionData?: TransactionSlip | null;
  mode?: "full" | "printOnly";
  printType?: string;
  closeModalRef?: React.Ref<HTMLButtonElement>;
  printSlipRef?: React.Ref<HTMLDivElement>;
  canEditSales?: boolean;
  editOptionsLoading?: boolean;
  editOptionsError?: string;
  onEditSales?: () => void;
  showEditMenu?: boolean;
  onEditMenu?: () => void;
  onPrintReceipt?: () => void;
  onPrintInvoice?: () => void;
  onPrint?: () => void;
};

// mode="full" is the Sales History list's view (Edit Sales / Edit Menu / Reprint Receipt / Reprint Invoice).
// mode="printOnly" is used when this modal is opened from the payment-completion screen: it only
// offers Cancel and a single Print button for whichever document type (receipt/invoice) was chosen there.
const SalesSlipViewModal = ({
  transactionData,
  mode = "full",
  printType,
  closeModalRef,
  printSlipRef,
  canEditSales,
  editOptionsLoading,
  editOptionsError,
  onEditSales,
  showEditMenu,
  onEditMenu,
  onPrintReceipt,
  onPrintInvoice,
  onPrint,
}: SalesSlipViewModalProps) => {
  const { t } = useTranslation();

  return (
    <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable ">
      <div className="modal-content" style={{ borderRadius: "10px" }}>
        <div className="modal-header d-flex justify-content-between">
          <h1 className="modal-title fs-5 text-center w-100" id="exampleModalLabel">
            {t('label_slip_number')} : {String(transactionData?.slip_number)?.padStart(4, "0") || ""}
          </h1>
          <button type="button" className="btn btn-icon" data-bs-dismiss="modal" aria-label="Close" id="close_modal" ref={closeModalRef}>
            <i className="ti ti-x"></i>
          </button>
        </div>

        <div className="modal-body p-0" >
          <div className="sales-slip-details-box">
            <div className="sales list-info" style={{ width: "100%", borderRight: "none" }}>
              <ul>
                <li>
                  <div className="list-info-left">
                    <span style={{ color: "black" }}>{t("label_source")}</span>
                  </div>
                  <div className="list-info-right">
                    <span>{transactionData?.source === "bulk_settlement" ? t("label_bulk_settlement") : t("label_normal_settlement")}</span>
                  </div>
                </li>
                <li>
                  <div className="list-info-left">
                    <span style={{ color: "black" }}>{t("label_person_in_charge")}</span>
                  </div>
                  <div className="list-info-right">
                    <span>{transactionData?.cashier_id?.name}</span>
                  </div>
                </li>
                <li>
                  <div className="list-info-left">
                    <span style={{ color: "black" }}>{t("label_table_no")}</span>
                  </div>
                  <div className="list-info-right">
                    <span>{transactionData?.tableDetail?.table_number}</span>
                  </div>
                </li>
                <li>
                  <div className="list-info-left">
                    <span style={{ color: "black" }}>{t("label_no_of_people")}</span>
                  </div>
                  <div className="list-info-right">
                    <span>
                      {t("label_all")} : {transactionData?.number_of_persons || 0}
                    </span>
                    <br></br>
                    <span>
                      {t("label_men")} : {transactionData?.men || 0}
                    </span>
                    <br></br>
                    <span>
                      {t("label_woman")}: {transactionData?.women || 0}
                    </span>
                  </div>
                </li>
                <li>
                  <div className="list-info-left">
                    <span style={{ color: "black" }}>{t("label_choose_payment_method")}</span>
                  </div>
                  <div className="list-info-right">
                    <span>{capitalizeFirstLetter(t(`label_${transactionData?.payment_method}`))}</span>
                  </div>
                </li>

                {transactionData?.payment_method !== "cash" && <li>
                  <div className="list-info-left">
                    <span style={{ color: "black" }}>{t("label_method_name")}</span>
                  </div>
                  <div className="list-info-right">
                    <span>{capitalizeFirstLetter(transactionData?.method_name)}</span>
                  </div>
                </li>}

                <li>
                  <div className="list-info-left">
                    <span style={{ color: "black" }}>{t("label_date_time")}</span>
                  </div>
                  <div className="list-info-right">
                    <span>{formateDate(transactionData?.createdAt, "YYYY/MM/DD", true, false)}</span>
                  </div>
                </li>
                <li>
                  <div className="list-info-left">
                    <span style={{ color: "black" }}>{t("label_type")}</span>
                  </div>
                  <div className="list-info-right">
                    <span>{transactionData?.type === "cancelled" ? t("label_cancelled") : t("label_sales")}</span>
                  </div>
                </li>
                <li className="list-border-none">
                  <div className="list-info-left">
                    <span style={{ fontSize: "18px", fontWeight: 600, color: "black" }}>{t("label_sales")}</span>
                  </div>
                </li>
                <li className="list-border-none">
                  <div className="list-info-left">
                    <span style={{ color: "black" }}>{t("label_sub_total")}</span>
                  </div>
                  <div className="list-info-right">
                    <span>¥ {showPrice(transactionData?.subtotal)} </span>
                  </div>
                </li>
                <li className="list-border-none">
                  <div className="list-info-left">
                    <span style={{ color: "black" }}>{t("label_discount_if_applicable")}</span>
                  </div>
                  <div className="list-info-right">
                    <span>
                      {transactionData?.gift_card_applied || transactionData?.coupon_applied ? `¥ ${showPrice((transactionData?.great_grand_total || 0) - (transactionData?.amount_after_discount || 0))}` : 0}
                    </span>
                  </div>
                </li>
                <li className="list-border-none">
                  <div className="list-info-left">
                    <span style={{ color: "black" }}>{t("label_consumption_tax")}</span>
                  </div>
                  <div className="list-info-right">
                    <span>¥ {showPrice(transactionData?.tax_price || 0)}</span>
                  </div>
                </li>
                {
                  (transactionData?.service_charge || 0) > 0 &&
                  <li className="list-border-none">
                    <div className="list-info-left">
                      <span style={{ color: "black" }}>{t("service_charge")}</span>
                    </div>
                    <div className="list-info-right">
                      <span>¥ {showPrice(transactionData?.service_charge || 0)}</span>
                    </div>
                  </li>
                }

                <li className="list-border-none">
                  <div className="list-info-left">
                    <span style={{ color: "black" }}>{t("label_total_capitalize")}</span>
                  </div>
                  <div className="list-info-right">
                    <span>
                      <b>¥ {showPrice(transactionData?.amount_after_discount || 0, 3)}</b>
                    </span>
                    <br></br>
                    <span>
                      8% {t("tax_on_text")} ¥ {showPrice(transactionData?.taxable_amount_8)}
                    </span>
                    <br></br>
                    <span>
                      10% {t("tax_on_text")} ¥ {showPrice(transactionData?.taxable_amount_10)}
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="modal-footer justify-content-center ">
          {mode === "printOnly" ? (
            <div className="w-100 d-flex justify-content-center gap-2 mt-2">
              <button type="button" className="btn btn-secondary" style={{ width: "100px" }} data-bs-dismiss="modal">
                {t("btn_cancel")}
              </button>
              <button type="button" className="btn btn-primary" style={{ width: "100px" }} onClick={onPrint}>
                {printType === TRANSACTION_PRINT_TYPES.ROYUSHU ? t("label_print_invoice") : t("label_print_receipt")}
              </button>
            </div>
          ) : (
            <>
              {canEditSales && (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={editOptionsLoading}
                  onClick={onEditSales}
                >
                  {t("label_edit_sales")}
                </button>
              )}
              {editOptionsError && <div className="text-danger w-100 text-center mt-2">{editOptionsError}</div>}
              {showEditMenu && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onEditMenu}
                >
                  {t("label_edit_menu")}
                </button>
              )}
              <div className="w-100 d-flex justify-content-center gap-2 mt-2">
                <button type="button" className="btn btn-primary" style={{ width: "100px" }} onClick={onPrintReceipt}>
                  {t("label_reprint_receipt")}
                </button>
                <button type="button" className="btn btn-primary" style={{ width: "100px" }} onClick={onPrintInvoice}>
                  {t("label_reprint_invoice")}
                </button>
              </div>
            </>
          )}
          <div style={{ display: 'none' }} >
            <div ref={printSlipRef} className="container d-flex justify-content-center align-items-center">
              <div dangerouslySetInnerHTML={{ __html: transactionData?.receiptHTML ? transactionData?.receiptHTML : "N/A" }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesSlipViewModal;
